import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen, Headphones } from "lucide-react";
import { api } from "../lib/api";
import { BookCover } from "../components/BookCover";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import ShelfPicker from "../components/ShelfPicker";
import { langName } from "../components/LanguageSelect";

export default function BookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [book, setBook] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/books/${id}`);
        setBook(r.data);
        if (user) {
          try {
            const p = await api.get(`/progress/${id}`);
            setProgress(p.data.progress);
          } catch { /* noop */ }
        }
      } catch {
        setBook(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, user]);

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-20 font-ui text-muted-foreground">opening the case…</div>;
  if (!book) return <div className="max-w-5xl mx-auto px-6 py-20 font-display text-3xl">volume not on record.</div>;

  const startPage = progress?.page ?? 0;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
      <Link to="/" className="inline-flex items-center gap-2 font-ui text-xs tracking-caps text-muted-foreground hover:text-primary mb-10">
        <ArrowLeft className="w-3.5 h-3.5" /> back to shelves
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <div className="md:sticky md:top-24 max-w-[280px] mx-auto md:mx-0">
            <BookCover book={book} size="xl" />
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="font-ui text-xs tracking-caps text-muted-foreground mb-4">
            {book.genre} <span className="mx-2 text-primary">·</span> {book.year >= 0 ? book.year : `${Math.abs(book.year)} bce`}
            {book.language && <><span className="mx-2 text-primary">·</span> <span data-testid="book-language">{langName(book.language)}</span></>}
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight">
            {book.title}
          </h1>
          <div className="mt-3 font-reader text-lg italic text-muted-foreground">
            by {book.author}
          </div>

          <div className="rule-ornament font-ui text-[10px] tracking-caps my-8">
            <span>synopsis</span>
          </div>

          <p className="font-reader text-lg md:text-xl leading-[1.7]">
            {book.synopsis}
          </p>

          {progress && (
            <div className="mt-8 p-4 rounded-md bg-secondary border border-border">
              <div className="font-ui text-xs tracking-caps text-muted-foreground">last read</div>
              <div className="font-display text-lg mt-1">page {progress.page + 1}</div>
            </div>
          )}

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => navigate(`/read/${book.id}${startPage ? `?p=${startPage}` : ""}`)}
              data-testid="start-reading-btn"
              className="rounded-full font-ui px-7 py-6 text-sm"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              {progress ? "continue reading" : "start reading"}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/read/${book.id}?audio=1${startPage ? `&p=${startPage}` : ""}`)}
              data-testid="listen-btn"
              className="rounded-full font-ui px-7 py-6 text-sm border-border"
            >
              <Headphones className="w-4 h-4 mr-2" /> listen as audiobook
            </Button>
          </div>

          {user && <div className="mt-4"><ShelfPicker bookId={book.id} /></div>}

          <p className="mt-6 font-ui text-xs text-muted-foreground">
            text sourced from project gutenberg. free forever.
          </p>
        </div>
      </div>
    </div>
  );
}
